import jsPDF from "jspdf";
import { guardarPDF } from "../pages/compras/helpers";

// La tabla del editor nunca entra en el documento del renderer. Sólo hay un bloque
// financiero aislado y un bitmap de página a la vez; el PDF conserva todos los tramos.
const AZUL = [46, 109, 164];
const A4_W = 210, A4_H = 297, MARGEN = 10, ANCHO = A4_W - MARGEN * 2, ARRIBA = 14, ABAJO = A4_H - 14;
const ANCHO_CSS = 1000, MAX_PIXELES = 1800000, MAX_LADO = 2048, MAX_ESCALA = 1.25;
const activas = new WeakSet();

function comprobarCancelacion(signal) {
  if (signal?.aborted) {
    const error = new Error("Exportación cancelada");
    error.name = "AbortError";
    throw error;
  }
}

function fallo(mensaje) {
  const error = new Error(mensaje);
  error.pdfMensaje = mensaje;
  return error;
}

function esperar(promesa, signal, limite = 15000) {
  comprobarCancelacion(signal);
  return new Promise((resolve, reject) => {
    let timer;
    const terminar = (fn, valor) => { clearTimeout(timer); signal?.removeEventListener("abort", cancelar); fn(valor); };
    const cancelar = () => { const e = new Error("Exportación cancelada"); e.name = "AbortError"; terminar(reject, e); };
    signal?.addEventListener("abort", cancelar, { once: true });
    timer = setTimeout(() => terminar(reject, fallo("El informe tardó demasiado en preparar sus recursos. Volvé a intentar.")), limite);
    Promise.resolve(promesa).then(v => terminar(resolve, v), e => terminar(reject, e));
  });
}

async function ceder(signal, ms = 0) {
  await esperar(new Promise(resolve => setTimeout(resolve, ms)), signal);
}

async function esperarGraficos(raiz, signal) {
  let previo = null;
  for (let i = 0; i < 24; i++) {
    comprobarCancelacion(signal);
    const svgs = Array.from(raiz.querySelectorAll("svg.recharts-surface"));
    const firma = svgs.map(sv => sv.innerHTML).join("|");
    const listas = svgs.every(sv => !sv.querySelector(".recharts-pie-sector") || sv.querySelector("text"));
    if (listas && firma === previo) return;
    previo = firma;
    await ceder(signal, 250);
  }
}

function dimensionesCaptura(ancho, alto) {
  if (![ancho, alto].every(n => Number.isFinite(n) && n > 0)) throw fallo("No se pudo preparar el tamaño de una sección del informe.");
  let scale = Math.min(MAX_ESCALA, MAX_LADO / ancho, MAX_LADO / alto, Math.sqrt(MAX_PIXELES / (ancho * alto)));
  while (Math.ceil(ancho * scale) * Math.ceil(alto * scale) > MAX_PIXELES) scale *= 0.999;
  return { width: ancho, height: alto, scale };
}

// Las coordenadas pertenecen al documento de impresión ya expandido. Si una fila
// o gráfico cabe entero, el corte se desplaza a su inicio; los elementos mayores
// que una página se recorren completos, sin saltar ni repetir píxeles.
function siguienteCorte(inicio, total, capacidad, intervalos) {
  const limite = Math.min(total, inicio + capacidad);
  let corte = limite;
  for (let i = intervalos.length - 1; i >= 0; i--) {
    const [arriba, abajo] = intervalos[i];
    if (arriba > inicio + 1 && arriba < corte && abajo > corte && abajo - arriba <= capacidad) corte = arriba;
  }
  return corte > inicio + 1 ? corte : limite;
}

async function prepararDocumento(fuente, frame, signal) {
  const cargado = new Promise(resolve => frame.addEventListener("load", resolve, { once: true }));
  frame.srcdoc = "<!doctype html><html><head><meta charset='utf-8'></head><body></body></html>";
  fuente.body.appendChild(frame);
  await esperar(cargado, signal);
  const d = frame.contentDocument;
  if (!d?.body) throw fallo("No se pudo abrir el documento temporal del informe.");
  const cargas = [];
  for (const original of fuente.head.querySelectorAll('style, link[rel="stylesheet"]')) {
    const copia = d.importNode(original, true);
    if (copia.tagName === "LINK") {
      copia.href = original.href;
      cargas.push(new Promise((resolve, reject) => { copia.onload = resolve; copia.onerror = () => reject(fallo("No se pudieron preparar los estilos del informe.")); }));
    }
    d.head.appendChild(copia);
  }
  const estilo = d.createElement("style");
  estilo.textContent = `html,body{margin:0;padding:0;background:white;width:${ANCHO_CSS}px}*,*:before,*:after{box-sizing:border-box;min-width:0}button,[data-pdf-ignore],script{display:none!important}.truncate,[class*="whitespace-nowrap"]{white-space:normal!important;overflow:visible!important;text-overflow:clip!important}table{width:100%!important;min-width:0!important;table-layout:fixed}th,td{white-space:normal!important;overflow-wrap:anywhere}input,textarea,select{max-width:100%}*{animation:none!important;transition:none!important}`;
  d.head.appendChild(estilo);
  await esperar(Promise.all(cargas), signal);
  return d;
}

// Recharts conserva las medidas de la pantalla (y un ancestro de ancho cero).
// Sólo en la copia de impresión se vuelve a medir el ancho disponible; la leyenda
// ocupa su propio espacio y no queda superpuesta o fuera del recorte del gráfico.
function prepararContenedoresGraficos(copia) {
  for (const cont of copia.querySelectorAll(".recharts-responsive-container")) {
    Object.assign(cont.style, { width: "100%", height: "auto", minWidth: "0", overflow: "visible" });
  }
  for (const wrapper of copia.querySelectorAll(".recharts-wrapper")) {
    Object.assign(wrapper.style, { width: "100%", height: "auto", minWidth: "0", maxWidth: "none", overflow: "visible" });
    if (wrapper.parentElement) Object.assign(wrapper.parentElement.style, { width: "100%", height: "auto", minWidth: "0", overflow: "visible" });
  }
  for (const leyenda of copia.querySelectorAll(".recharts-legend-wrapper")) {
    Object.assign(leyenda.style, { position: "static", width: "100%", height: "auto", left: "auto", right: "auto", top: "auto", bottom: "auto", transform: "none", marginTop: "8px" });
    for (const lista of leyenda.querySelectorAll("ul")) Object.assign(lista.style, { display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "4px 12px", width: "100%", height: "auto", padding: "0", margin: "0", listStyle: "none" });
    for (const item of leyenda.querySelectorAll("li")) Object.assign(item.style, { display: "inline-flex", alignItems: "center", margin: "0", whiteSpace: "normal", maxWidth: "100%" });
    for (const texto of leyenda.querySelectorAll("span")) Object.assign(texto.style, { whiteSpace: "normal", overflowWrap: "anywhere" });
    // En el DOM de Recharts la leyenda precede al SVG; en impresión va debajo.
    leyenda.parentNode?.appendChild(leyenda);
  }
  for (const tooltip of copia.querySelectorAll(".recharts-tooltip-wrapper")) tooltip.style.display = "none";
}

function congelarSvg(original, destino, d) {
  const vb = original.viewBox?.baseVal;
  const rect = original.getBoundingClientRect();
  const anchoFuente = rect.width || Number(original.getAttribute("width")) || vb?.width;
  const altoFuente = rect.height || Number(original.getAttribute("height")) || vb?.height;
  const disponible = destino.parentElement?.getBoundingClientRect().width || anchoFuente;
  const ancho = Math.min(anchoFuente, disponible);
  const alto = ancho * altoFuente / anchoFuente;
  const limite = dimensionesCaptura(ancho, alto);
  const factor = Math.min(1, limite.scale);
  const pixW = Math.max(1, Math.floor(ancho * factor)), pixH = Math.max(1, Math.floor(alto * factor));
  const svg = original.cloneNode(true);
  const elementos = [original, ...original.querySelectorAll("*")];
  const duplicados = [svg, ...svg.querySelectorAll("*")];
  const propiedades = ["color", "fill", "fill-opacity", "fill-rule", "stroke", "stroke-width", "stroke-opacity", "stroke-linecap", "stroke-linejoin", "stroke-miterlimit", "stroke-dasharray", "stroke-dashoffset", "opacity", "font-family", "font-size", "font-style", "font-weight", "font-stretch", "letter-spacing", "word-spacing", "text-anchor", "dominant-baseline", "alignment-baseline", "paint-order", "vector-effect", "visibility", "display", "shape-rendering", "text-rendering", "clip-path", "mask", "filter", "marker-start", "marker-mid", "marker-end", "stop-color", "stop-opacity", "flood-color", "flood-opacity"];
  elementos.forEach((el, i) => {
    const cs = original.ownerDocument.defaultView.getComputedStyle(el);
    for (const propiedad of propiedades) {
      // getComputedStyle puede absolutizar una referencia a un defs local.
      const valor = cs.getPropertyValue(propiedad).replace(/url\(["']?[^)"']*#([^)"']+)["']?\)/g, "url(#$1)");
      if (valor) duplicados[i].style.setProperty(propiedad, valor);
    }
  });
  svg.querySelectorAll("script").forEach(el => el.remove());
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("width", String(pixW)); svg.setAttribute("height", String(pixH));
  if (!svg.getAttribute("viewBox")) svg.setAttribute("viewBox", `0 0 ${anchoFuente} ${altoFuente}`);
  if (!svg.getAttribute("preserveAspectRatio")) svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  Object.assign(svg.style, { width: `${pixW}px`, height: `${pixH}px`, maxWidth: "none", maxHeight: "none" });
  const imagen = d.createElement("img");
  imagen.setAttribute("data-pdf-svg", "1");
  imagen.alt = original.getAttribute("aria-label") || "Gráfico del informe";
  imagen.width = pixW; imagen.height = pixH;
  const cs = original.ownerDocument.defaultView.getComputedStyle(original);
  Object.assign(imagen.style, { width: `${ancho}px`, height: `${alto}px`, maxWidth: "100%", display: cs.display === "inline-block" || cs.display === "inline" ? "inline-block" : "block", verticalAlign: cs.verticalAlign, marginRight: cs.marginRight, flexShrink: "0" });
  // Data URI evita URLs temporales y referencias externas al documento original.
  // Al quitar el IMG al terminar cada bloque también se suelta el SVG serializado.
  imagen.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(svg))}`;
  destino.parentNode?.replaceChild(imagen, destino);
}

function copiarBloque(original, d) {
  const copia = d.importNode(original, true);
  if (copia.querySelector("iframe, object, embed")) throw fallo("Una sección contiene un documento incrustado que no se pudo preparar para impresión.");
  const originales = [original, ...original.querySelectorAll("*")];
  const copias = [copia, ...copia.querySelectorAll("*")];
  const graficos = originales.flatMap((el, i) => el.tagName.toLowerCase() === "svg" && !el.parentElement?.closest("svg") ? [[el, copias[i]]] : []);
  // Se sincroniza el valor actual, que puede no coincidir con el atributo HTML.
  originales.forEach((el, i) => {
    const dest = copias[i];
    if (!dest) return;
    if (el.tagName === "CANVAS") {
      const factor = Math.min(1, Math.sqrt(MAX_PIXELES / Math.max(1, el.width * el.height)), MAX_LADO / Math.max(1, el.width), MAX_LADO / Math.max(1, el.height));
      dest.width = Math.max(1, Math.floor(el.width * factor)); dest.height = Math.max(1, Math.floor(el.height * factor));
      if (el.width && el.height) dest.getContext("2d").drawImage(el, 0, 0, dest.width, dest.height);
    }
    if (el.tagName === "INPUT") { dest.value = el.value; dest.checked = el.checked; }
    if (el.tagName === "TEXTAREA") dest.value = el.value;
    if (el.tagName === "SELECT") dest.selectedIndex = el.selectedIndex;
    const esCampo = el.tagName === "TEXTAREA" || el.tagName === "SELECT" || (el.tagName === "INPUT" && !["checkbox", "radio", "hidden", "file", "range", "color", "date", "time"].includes(el.type));
    if (!esCampo) return;
    const cs = el.ownerDocument.defaultView.getComputedStyle(el);
    const r = el.getBoundingClientRect();
    const caja = d.createElement("div");
    caja.className = el.className; caja.style.cssText = el.style.cssText;
    caja.textContent = el.tagName === "SELECT" ? (el.selectedOptions[0]?.textContent || "") : (el.value || el.placeholder || "");
    Object.assign(caja.style, { boxSizing: "border-box", minHeight: `${r.height}px`, height: "auto", minWidth: "0", maxWidth: "100%", display: "block", textAlign: cs.textAlign, overflow: "visible", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: "1.2" });
    if (!el.value) caja.style.color = "#9CA3AF";
    caja.setAttribute("data-pdf-valor", "1");
    dest.parentNode?.replaceChild(caja, dest);
  });
  copia.querySelectorAll("script").forEach(el => el.remove());
  const tipografia = original.ownerDocument.defaultView.getComputedStyle(original);
  d.body.style.fontFamily = tipografia.fontFamily;
  d.body.style.fontSize = tipografia.fontSize;
  d.body.style.color = tipografia.color;
  copia.style.width = `${ANCHO_CSS}px`; copia.style.maxWidth = "none";
  d.body.replaceChildren(copia);
  for (const el of [copia, ...copia.querySelectorAll("*")]) {
    const cs = d.defaultView.getComputedStyle(el);
    if ([cs.overflow, cs.overflowX, cs.overflowY].some(v => ["auto", "scroll", "hidden"].includes(v))) {
      if (el.scrollHeight > el.clientHeight + 1) el.style.height = "auto";
      el.style.overflow = "visible"; el.style.maxHeight = "none";
    }
  }
  prepararContenedoresGraficos(copia);
  for (const [fuente, destino] of graficos) congelarSvg(fuente, destino, d);
  return copia;
}

async function bytesCanvas(canvas, signal) {
  const blob = await new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(fallo("No se pudo codificar una página del informe.")), "image/jpeg", 0.92));
  comprobarCancelacion(signal);
  return new Uint8Array(await blob.arrayBuffer());
}

function vaciarDocumento(d) {
  if (!d?.body) return;
  for (const canvas of d.body.querySelectorAll("canvas")) {
    canvas.width = 0; canvas.height = 0; canvas.remove();
  }
  for (const imagen of d.body.querySelectorAll("img[data-pdf-svg]")) {
    imagen.removeAttribute("src"); imagen.remove();
  }
  d.body.replaceChildren();
}

export async function pdfAnalisisFinanciero({ raiz, selectores, titulo, lineas, archivo, signal, onProgress }) {
  comprobarCancelacion(signal);
  if (!raiz || activas.has(raiz)) throw fallo("Ya se está preparando un PDF. Esperá o cancelá el anterior.");
  activas.add(raiz);
  let frame, copia, doc;
  try {
    const bloques = selectores.map(sel => raiz.querySelector(`[data-testid="${sel}"]`));
    if (!bloques.length || bloques.some(el => !el)) throw fallo("El informe todavía está cargando. Esperá a que estén listas sus secciones y volvé a intentar.");
    onProgress?.({ fase: "preparando", bloque: 0, total: bloques.length, pagina: 1 });
    await ceder(signal);
    const { default: html2canvas } = await import("html2canvas");
    comprobarCancelacion(signal);
    await esperarGraficos(raiz, signal);
    frame = raiz.ownerDocument.createElement("iframe");
    frame.setAttribute("aria-hidden", "true"); frame.setAttribute("title", "Documento temporal de impresión");
    Object.assign(frame.style, { position: "fixed", left: "-100000px", top: "0", width: "1280px", height: "1500px", border: "0", pointerEvents: "none" });
    const d = await prepararDocumento(raiz.ownerDocument, frame, signal);
    doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    let y = ARRIBA;
    const nuevaPagina = () => { doc.addPage(); y = ARRIBA; };
    const texto = (valor, paso) => {
      for (const linea of doc.splitTextToSize(String(valor), ANCHO)) {
        if (y + paso > ABAJO) nuevaPagina();
        doc.text(linea, MARGEN, y); y += paso;
      }
    };
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...AZUL);
    texto(titulo, 6);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(70, 70, 70);
    for (const l of (lineas || []).filter(Boolean)) texto(l, 4);
    y += 2;
    for (let indice = 0; indice < bloques.length; indice++) {
      comprobarCancelacion(signal);
      copia = copiarBloque(bloques[indice], d);
      // Fuentes e imágenes pertenecen exclusivamente a este bloque aislado.
      if (d.fonts?.ready) await esperar(d.fonts.ready, signal);
      await esperar(Promise.all(Array.from(copia.querySelectorAll("img")).map(img => img.decode ? img.decode() : Promise.resolve())), signal);
      const rect = copia.getBoundingClientRect();
      const alto = Math.ceil(Math.max(rect.height, copia.scrollHeight));
      if (!Number.isFinite(alto) || alto <= 0 || copia.scrollWidth > ANCHO_CSS + 2) throw fallo("No se pudo preparar el tamaño de una sección del informe sin recortar su contenido.");
      const intervalos = Array.from(copia.querySelectorAll("tr, svg, img[data-pdf-svg], p, h1, h2, h3, h4, li, [data-pdf-valor]")).map(el => {
        const r = el.getBoundingClientRect(); return [Math.max(0, r.top - rect.top), Math.min(alto, r.bottom - rect.top)];
      }).filter(([a, b]) => b > a).sort((a, b) => a[0] - b[0]);
      let offset = 0;
      while (offset < alto) {
        comprobarCancelacion(signal);
        if (ABAJO - y < 40) nuevaPagina();
        const capacidad = Math.max(1, Math.floor((ABAJO - y) * ANCHO_CSS / ANCHO));
        const corte = siguienteCorte(offset, alto, capacidad, intervalos);
        const height = corte - offset;
        onProgress?.({ fase: "capturando", bloque: indice + 1, total: bloques.length, pagina: doc.getNumberOfPages() });
        await ceder(signal);
        let canvas, bytes;
        try {
          canvas = await html2canvas(copia, { ...dimensionesCaptura(ANCHO_CSS, height), x: 0, y: offset,
            windowWidth: 1280, windowHeight: 1500, scrollX: 0, scrollY: 0, backgroundColor: "#ffffff", logging: false, useCORS: true, removeContainer: true });
          comprobarCancelacion(signal);
          if (canvas.width <= 0 || canvas.height <= 0 || canvas.width > MAX_LADO || canvas.height > MAX_LADO || canvas.width * canvas.height > MAX_PIXELES) throw fallo("No se pudo generar una página dentro del tamaño permitido.");
          bytes = await bytesCanvas(canvas, signal);
          comprobarCancelacion(signal);
          canvas.width = 0; canvas.height = 0; canvas.remove(); canvas = null;
          doc.addImage(bytes, "JPEG", MARGEN, y, ANCHO, height * ANCHO / ANCHO_CSS, undefined, "FAST");
        } finally {
          bytes = null;
          if (canvas) { canvas.width = 0; canvas.height = 0; canvas.remove(); }
        }
        offset = corte; y += height * ANCHO / ANCHO_CSS + 3;
        if (offset < alto) nuevaPagina();
      }
      vaciarDocumento(d); copia = null;
    }
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      comprobarCancelacion(signal);
      doc.setPage(i); doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120, 120, 120);
      doc.text(`CEN Ingeniería · ${titulo} · Página ${i} de ${n}`, A4_W / 2, A4_H - 6, { align: "center" });
      if (i % 16 === 0) await ceder(signal);
    }
    comprobarCancelacion(signal);
    guardarPDF(doc, archivo);
    onProgress?.({ fase: "listo", bloque: bloques.length, total: bloques.length, pagina: n });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    throw fallo(error?.pdfMensaje || "No se pudo generar el PDF. Volvé a intentar cuando el informe termine de cargar.");
  } finally {
    copia = null; doc = null;
    if (frame) { try { vaciarDocumento(frame.contentDocument); } finally { frame.remove(); } }
    activas.delete(raiz);
  }
}
